import { useParams, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  LAUNCH_EDITIONS,
  CLUB_EDITIONS,
  DESIGN_ARCHIVE,
  SCENIC_EDITIONS,
  SCENIC_PETITE_SIZE_SUITE,
  PETITE_LIFESTYLE,
  CLUB_SCHEMATICS,
} from "@/lib/watchImages";
import { lookupAllocation, formatUSD } from "@/lib/allocation";
import WatchPreviewModal from "@/components/site/WatchPreviewModal";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import WatchImage from "@/components/site/WatchImage";

const CATALOGS = {
  launch: {
    title: "Launch Editions",
    subtitle: "The opening run · 1,500 of each · 18K gold",
    intro:
      "Four metals, four dials. The founding chapter of COURSE I — the watch from which the house begins.",
    cta: "Reserve an allocation",
    watches: LAUNCH_EDITIONS,
    cardWidth: "w-full sm:w-1/2 lg:w-1/4",
    extra: null,
    ambient: "lux-clubhouse-mansion",
  },
  club: {
    title: "Club Editions",
    subtitle: "Stainless steel · 100 pieces per club",
    intro:
      "Stainless steel, commissioned one club at a time. 100 pieces per club, no re-runs — once a club's hundred are out, the reference is closed for good.",
    cta: "Inquire about a club commission",
    watches: CLUB_EDITIONS,
    cardWidth: "w-full sm:w-1/2 lg:w-1/3",
    extra: { title: "Design Archive", watches: DESIGN_ARCHIVE, note: "Earlier directions — never produced.", cardWidth: "w-full sm:w-1/3" },
    schematics: CLUB_SCHEMATICS,
    ambient: "lux-clubhouse-mansion",
  },
  "scenic-dial": {
    title: "Scenic Dial",
    subtitle: "Hand-painted dials · 41 mm stainless",
    intro:
      "Hand-painted miniatures of the courses that matter — one dial at a time, on the same Swiss movement, in the same 41 mm case.",
    cta: "Submit a scenic commission",
    watches: SCENIC_EDITIONS.filter((w) => w.line === "Scenic Dial"),
    cardWidth: "w-full sm:w-1/2 lg:w-1/3",
    extra: null,
    ambient: "lux-cliffside-sunset",
  },
  "scenic-petite": {
    title: "Scenic Petite",
    subtitle: "28 · 30 · 32 mm · for women who actually play",
    intro:
      "The Scenic Dial refined into three smaller proportions for the women's line. Same painted dials, same Swiss movement, same restraint on the face — built for the wrist that actually plays.",
    cta: "Reserve a Petite allocation",
    watches: SCENIC_EDITIONS.filter((w) => w.line === "Scenic Petite"),
    cardWidth: "w-full sm:w-1/2 lg:w-1/3",
    extra: { title: "The Petite Suite", suite: SCENIC_PETITE_SIZE_SUITE },
    editorial: PETITE_LIFESTYLE,
    ambient: "lux-cliffside-sunset",
  },
};

export default function CollectionCatalog() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const catalog = CATALOGS[slug];
  const [highlightSlug, setHighlightSlug] = useState(null);
  const [openWatch, setOpenWatch] = useState(null);

  // Deep-link from Allocation page: /collection/<slug>#<watch-slug>
  // Scroll to the target watch card and apply a brief gold highlight.
  useEffect(() => {
    if (!hash || !catalog) return;
    const targetSlug = hash.replace(/^#/, "");
    const el = document.querySelector(
      `[data-testid="catalog-watch-${targetSlug}"]`,
    );
    if (!el) return;
    // Wait one frame so any motion entrance doesn't fight the scroll
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightSlug(targetSlug);
      setTimeout(() => setHighlightSlug(null), 2200);
    }, 250);
    return () => clearTimeout(t);
  }, [hash, catalog]);

  if (!catalog) {
    return (
      <main className="bg-[#0C0F0A] text-[#F5F2EB] min-h-screen">
        <Nav />
        <div className="max-w-3xl mx-auto px-6 py-40 text-center">
          <h1 className="font-serif-display text-5xl">Not found.</h1>
          <p className="mt-6 text-[#A8A9AD]">
            That collection doesn&apos;t exist (yet).
          </p>
          <Link
            to="/"
            data-testid="catalog-back-home"
            className="inline-block mt-10 text-[#C5A059] underline-offset-4 hover:underline"
          >
            Back to CLEMENT
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main
      className="bg-[#0C0F0A] text-[#F5F2EB] min-h-screen"
      data-testid={`catalog-${slug}`}
    >
      <Nav />

      {/* Header */}
      <section className="relative pt-40 sm:pt-48 pb-16 sm:pb-20 border-b border-[rgba(197,160,89,0.15)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/#collections"
            data-testid="catalog-back-link"
            className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD] hover:text-[#C5A059] transition-colors"
          >
            ← Back to all collections
          </Link>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <span className="overline">{catalog.subtitle}</span>
              <h1 className="mt-4 font-serif-display text-[#F5F2EB] font-light text-balance text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
                {catalog.title}
              </h1>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#A8A9AD] text-base sm:text-lg leading-relaxed">
                {catalog.intro}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Watch grid — with optional luxury ambient backdrop */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        {catalog.ambient && <AmbientBackdrop assetKey={catalog.ambient} />}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          {catalog.schematics && <ClubSchematics schematics={catalog.schematics} />}

          <div
            className="flex flex-wrap justify-center"
            data-testid="catalog-watch-grid"
          >
            {catalog.watches.map((w, i) => (
              <CatalogCard
                key={w.slug}
                edition={w}
                index={i}
                widthClass={catalog.cardWidth || "w-full sm:w-1/2 lg:w-1/3"}
                highlighted={highlightSlug === w.slug}
                onOpen={() => setOpenWatch(w)}
              />
            ))}
          </div>

          {/* Extra: Design archive OR Petite size-suite */}
          {catalog.extra?.watches && (
            <div className="mt-24">
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <div className="overline">{catalog.extra.title}</div>
                  <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F2EB] mt-3 font-light">
                    {catalog.extra.note}
                  </h2>
                </div>
              </div>
              <div className="mt-10 flex flex-wrap justify-center">
                {catalog.extra.watches.map((w, i) => (
                  <CatalogCard
                    key={w.slug}
                    edition={w}
                    index={i}
                    widthClass={catalog.extra.cardWidth || "w-full sm:w-1/3"}
                    muted
                  />
                ))}
              </div>
            </div>
          )}

          {catalog.extra?.suite && (
            <SizeSuite suite={catalog.extra.suite} />
          )}

          {catalog.editorial && (
            <EditorialGallery items={catalog.editorial} />
          )}

          {/* CTA */}
          <div className="mt-24 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 border-t border-[rgba(197,160,89,0.2)] pt-12">
            <p className="text-[#A8A9AD] text-sm max-w-md leading-relaxed">
              Allocations and target figures are listed in full on the{" "}
              <Link
                to="/allocation"
                className="text-[#C5A059] hover:text-[#F5F2EB] underline-offset-4 hover:underline"
              >
                Allocation page
              </Link>
              . Releases are reserved exclusively through the Private Registry.
            </p>
            <Link
              to="/#waitlist"
              data-testid="catalog-cta-waitlist"
              className="inline-flex items-center gap-3 border border-[#C5A059] text-[#C5A059] px-8 py-4 text-[11px] uppercase tracking-[0.32em] hover:bg-[#C5A059] hover:text-[#0C0F0A] transition-colors"
            >
              {catalog.cta}
              <span aria-hidden className="inline-block w-6 h-px bg-current" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <WatchPreviewModal
        watch={openWatch}
        onClose={() => setOpenWatch(null)}
      />
    </main>
  );
}

function AmbientBackdrop({ assetKey }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      data-testid="catalog-ambient-backdrop"
      aria-hidden="true"
    >
      <img
        src={`${API}/asset/${assetKey}`}
        alt=""
        className="w-full h-full object-cover object-center opacity-40"
      />
      {/* Gradient washes so the watches stay the hero — softer than before */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0C0F0A] via-[#0C0F0A]/55 to-[#0C0F0A]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(197,160,89,0.06),transparent_70%)]" />
    </div>
  );
}

function AllocationPill({ slug }) {
  const a = lookupAllocation(slug);
  if (!a) {
    return (
      <div
        className="mt-5 px-5 py-4 border border-[rgba(245,242,235,0.14)] bg-[#0c1410]"
        data-testid={`allocation-pill-${slug}`}
      >
        <div className="text-[9px] uppercase tracking-[0.42em] text-[#A8A9AD]/80 mb-1.5">
          Allocation
        </div>
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#A8A9AD] italic">
          Price by application
        </div>
      </div>
    );
  }
  return (
    <Link
      to="/allocation"
      data-testid={`allocation-pill-${slug}`}
      className="group/pill mt-5 block px-5 py-4 border border-[#C5A059] bg-[#0c1410] hover:bg-[#1a2d22] transition-colors"
    >
      <div className="text-[9px] uppercase tracking-[0.42em] text-[#C5A059] mb-2">
        {a.tier} · Tier
      </div>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <span className="font-serif-display text-[20px] sm:text-xl text-[#F5F2EB] font-light leading-none tracking-tight whitespace-nowrap">
          {formatUSD(a.priceUSD)}
        </span>
        <span className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD] whitespace-nowrap">
          {a.pieces} pieces
        </span>
      </div>
    </Link>
  );
}

function CatalogCard({ edition: w, index, widthClass, muted = false, highlighted = false, onOpen }) {
  return (
    <motion.figure
      data-testid={`catalog-watch-${w.slug}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay: index * 0.06, ease: "easeOut" }}
      className={`group relative bg-[#0C0F0A]/85 backdrop-blur-sm p-6 sm:p-8 border -mt-px -ml-px box-border transition-all duration-500 ${
        highlighted
          ? "border-[#C5A059] shadow-[0_0_0_2px_rgba(197,160,89,0.45),0_30px_60px_-15px_rgba(197,160,89,0.35)] z-10"
          : "border-[rgba(197,160,89,0.15)]"
      } ${widthClass}`}
    >
      <div className="relative aspect-square w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#161B14] via-[#0C0F0A] to-[#0C0F0A]" />
        {/* Front face — fades out on hover when a back image exists */}
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${
            w.back ? "group-hover:opacity-0" : ""
          }`}
        >
          <WatchImage
            src={w.front}
            alt={`CLEMENT — ${w.name}`}
            fallbackTitle={w.name}
            fallbackSub={w.ref}
            loading={index < 3 ? "eager" : "lazy"}
            className={`transition-transform duration-700 group-hover:scale-105 ${
              muted ? "opacity-80" : ""
            }`}
            testId={`catalog-image-${w.slug}`}
          />
        </div>
        {/* Back face — fades in on hover */}
        {w.back && (
          <div
            className="absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
            data-testid={`catalog-image-back-${w.slug}`}
          >
            <WatchImage
              src={w.back}
              alt={`CLEMENT — ${w.name} caseback`}
              fallbackTitle={`${w.name} · Caseback`}
              fallbackSub={w.ref}
              loading="lazy"
              className="transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-[0.42em] text-[#C5A059]/80 pointer-events-none">
              Caseback
            </div>
          </div>
        )}
        {/* Open modal · zoom in lightbox */}
        {onOpen && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            data-testid={`catalog-zoom-${w.slug}`}
            aria-label={`Zoom into ${w.name}`}
            className="absolute top-3 right-3 z-10 inline-flex items-center gap-2 px-3 py-2 border border-[#C5A059]/50 bg-[#0C0F0A]/70 backdrop-blur-sm text-[9px] uppercase tracking-[0.42em] text-[#C5A059] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-[#C5A059] hover:text-[#0C0F0A]"
          >
            <span className="block w-3 h-px bg-current" />
            Zoom
          </button>
        )}
      </div>
      <figcaption className="mt-6">
        <div className="overline mb-3">{w.ref}</div>
        <h3 className="font-serif-display text-2xl sm:text-3xl text-[#F5F2EB] font-light leading-tight">
          {w.name}
        </h3>
        <p className="text-[#A8A9AD] text-sm leading-relaxed mt-3 min-h-[3.5rem]">
          {w.notes}
        </p>
        <div className="hairline mt-5" />
        <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-[#C5A059]">
          <span>{w.metal || "—"}</span>
          {w.pieces ? <span>{w.pieces}</span> : null}
        </div>
        <AllocationPill slug={w.slug} />
        {w.specs && <SpecSheet specs={w.specs} slug={w.slug} />}
      </figcaption>
    </motion.figure>
  );
}

function SpecSheet({ specs, slug }) {
  const rows = [
    ["Reference", specs.reference],
    ["Case", specs.case],
    ["Crown", specs.crown],
    ["Dial", specs.dial],
    ["Bezel", specs.bezel],
    ["Crystal", specs.crystal],
    ["Caseback", specs.caseback],
    ["Movement", specs.movement],
    ["Complications", specs.complications],
    ["Hands", specs.hands],
    ["Markers", specs.markers],
    ["Bracelet", specs.bracelet],
    ["Water resistance", specs.waterResistance],
    ["Production", specs.production],
  ].filter(([, v]) => Boolean(v));

  return (
    <details
      data-testid={`spec-sheet-${slug}`}
      className="mt-8 group/spec border border-[rgba(197,160,89,0.35)] bg-[#0c1410]/60"
    >
      <summary className="flex items-center justify-between gap-6 cursor-pointer list-none select-none px-5 py-4 border-b border-transparent group-open/spec:border-[rgba(197,160,89,0.25)] bg-gradient-to-r from-[#1a2d22] to-transparent hover:from-[#22382c] transition-colors">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-block w-6 h-px bg-[#C5A059] shrink-0" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#C5A059] font-medium whitespace-nowrap">
            Specs
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD] shrink-0 group-open/spec:hidden">
          + Open
        </span>
        <span className="text-[10px] uppercase tracking-[0.32em] text-[#C5A059] shrink-0 hidden group-open/spec:inline">
          − Close
        </span>
      </summary>
      <dl className="px-5 py-2 divide-y divide-[rgba(197,160,89,0.15)] text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="py-3.5">
            <dt className="text-[10px] uppercase tracking-[0.28em] text-[#C5A059] mb-1.5">
              {k}
            </dt>
            <dd className="text-[#d6d3c6] leading-relaxed text-[13px] sm:text-sm">
              {v}
            </dd>
          </div>
        ))}
      </dl>
      <p className="px-5 py-3 text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD] border-t border-[rgba(197,160,89,0.15)] bg-[#0c1410]/40">
        Prototype specifications · subject to refinement at the workshop
      </p>
    </details>
  );
}

function SizeSuite({ suite }) {
  return (
    <figure
      data-testid="catalog-size-suite"
      className="mt-24 relative bg-[#0C0F0A] border border-[rgba(197,160,89,0.2)] overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-5">
        <div className="lg:col-span-3 relative min-h-[420px] sm:min-h-[520px]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#161B14] via-[#0C0F0A] to-[#0C0F0A]" />
          <WatchImage
            src={suite.src}
            alt={`CLEMENT Scenic Petite — ${suite.title}`}
            fallbackTitle={suite.title}
            fallbackSub="28 · 30 · 32 mm"
            loading="eager"
            testId="catalog-size-suite-image"
          />
        </div>
        <figcaption className="lg:col-span-2 p-8 sm:p-12 flex flex-col justify-center">
          <div className="overline">{suite.title}</div>
          <h3 className="font-serif-display text-3xl sm:text-4xl text-[#F5F2EB] mt-4 font-light">
            Three sizes,{" "}
            <span className="italic text-[#C5A059]">same watch</span>.
          </h3>
          <p className="text-[#A8A9AD] text-sm sm:text-base leading-relaxed mt-5">
            28, 30 and 32 mm. Same painted dials, same movement — just
            three case sizes so you can pick what actually fits your wrist.
          </p>
          <div className="hairline mt-8" />
          <div className="mt-5 text-[10px] uppercase tracking-[0.28em] text-[#C5A059]">
            {suite.caption}
          </div>
        </figcaption>
      </div>
    </figure>
  );
}

function EditorialGallery({ items }) {
  return (
    <section className="mt-24" data-testid="catalog-editorial-gallery">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Worn on the course</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F2EB] mt-3 font-light">
            How the Petite actually wears.
          </h2>
        </div>
        <p className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD] max-w-xs">
          Members of the early CLEMENT set, photographed at their home clubs
        </p>
      </div>

      <div className="mt-10 grid grid-cols-12 gap-3 sm:gap-4">
        {/* Tall left: fairway cuff */}
        <figure
          data-testid={`editorial-${items[0].slug}`}
          className="col-span-12 sm:col-span-6 lg:col-span-5 relative aspect-[4/5] overflow-hidden group bg-[#1F382A]/15"
        >
          <WatchImage
            src={items[0].src}
            alt={items[0].title}
            fallbackTitle={items[0].title}
            fallbackSub="Scenic Petite"
            objectFit="cover"
            loading="eager"
            testId={`editorial-${items[0].slug}-image`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
          <figcaption className="absolute bottom-0 inset-x-0 p-5 sm:p-7 text-[#F5F2EB]">
            <div className="text-[9px] uppercase tracking-[0.32em] text-[#C5A059] mb-2">
              {items[0].caption}
            </div>
            <h3 className="font-serif-display text-xl sm:text-2xl">
              {items[0].title}
            </h3>
          </figcaption>
        </figure>

        {/* Right column stack: scorecard + putter */}
        <div className="col-span-12 sm:col-span-6 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {items.slice(1).map((it, idx) => (
            <figure
              key={it.slug}
              data-testid={`editorial-${it.slug}`}
              className={`relative overflow-hidden group bg-[#1F382A]/15 ${
                idx === 0 ? "aspect-square sm:col-span-2 lg:col-span-1" : "aspect-[4/5] lg:aspect-square"
              }`}
            >
              <WatchImage
                src={it.src}
                alt={it.title}
                fallbackTitle={it.title}
                fallbackSub="Scenic Petite"
                objectFit="cover"
                loading="lazy"
                testId={`editorial-${it.slug}-image`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
              <figcaption className="absolute bottom-0 inset-x-0 p-5 text-[#F5F2EB]">
                <div className="text-[9px] uppercase tracking-[0.32em] text-[#C5A059] mb-2">
                  {it.caption}
                </div>
                <h3 className="font-serif-display text-xl">{it.title}</h3>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}



function ClubSchematics({ schematics }) {
  return (
    <section
      data-testid="club-schematics-section"
      className="mb-20 sm:mb-28 space-y-10"
    >
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-3">
          <span className="inline-block w-10 h-px bg-[#C5A059]" />
          <span className="text-[11px] uppercase tracking-[0.32em] text-[#C5A059] font-medium">
            Premium Production Schematics
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD] hidden sm:inline">
          Reference standard · Club Edition
        </span>
      </div>

      {/* Side profile — full width, large */}
      <figure
        data-testid="schematic-side-figure"
        className="border border-[rgba(197,160,89,0.35)] bg-[#0c1410]"
      >
        <div className="flex items-center justify-between px-6 sm:px-10 py-4 border-b border-[rgba(197,160,89,0.25)]">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#C5A059]">
            Plate 01 · Side Profile &amp; Dimensions
          </div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD] hidden sm:inline">
            41 mm Ø · 47 mm L2L · 11.8–12.4 mm
          </div>
        </div>
        <div className="relative w-full bg-[#0c1410]" style={{ minHeight: 520 }}>
          <div className="aspect-[16/10] sm:aspect-[16/8]">
            <WatchImage
              src={schematics.side}
              alt="CLEMENT COURSE I Club Edition — side profile and dimensions"
              fallbackTitle="Side Profile"
              fallbackSub="Club Edition"
              loading="eager"
              testId="club-schematic-side-image"
            />
          </div>
        </div>
        <figcaption className="px-6 sm:px-10 py-5 border-t border-[rgba(197,160,89,0.2)] text-[#d6d3c6] text-sm leading-relaxed">
          41 mm diameter · 47 mm lug-to-lug · 11.8–12.4 mm thick · 6.5 mm screw-down dimpled golf-ball crown projecting 3.4–4.0 mm · 100 m water resistance · case in Grade 5 titanium or 904L stainless steel · sapphire crystal with internal AR.
        </figcaption>
      </figure>

      {/* Exploded view — own section, full width, even bigger */}
      <figure
        data-testid="schematic-exploded-figure"
        className="border border-[rgba(197,160,89,0.35)] bg-[#0c1410]"
      >
        <div className="flex items-center justify-between px-6 sm:px-10 py-4 border-b border-[rgba(197,160,89,0.25)]">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#C5A059]">
            Plate 02 · Exploded View &amp; Components
          </div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD] hidden sm:inline">
            12 components · Swiss automatic
          </div>
        </div>
        <div className="relative w-full bg-[#0c1410]">
          <div className="aspect-[3/4] sm:aspect-[4/3] max-h-[1100px]">
            <WatchImage
              src={schematics.exploded}
              alt="CLEMENT COURSE I Club Edition — exploded component view"
              fallbackTitle="Exploded View"
              fallbackSub="Club Edition"
              loading="eager"
              testId="club-schematic-exploded-image"
            />
          </div>
        </div>
        <figcaption className="px-6 sm:px-10 py-5 border-t border-[rgba(197,160,89,0.2)] text-[#d6d3c6] text-sm leading-relaxed space-y-2">
          <p>
            Top down: sapphire crystal with internal AR coating · brushed
            shield-bezel with plays-like-yardage scale · applied-marker
            textured dial with sub-registers at 3 / 6 / 9 · Sellita
            SW300-1 Swiss automatic with custom CLEMENT rotor ·
            individual movement holder · sapphire exhibition caseback ·
            integrated H-link bracelet with screw links and quick-release.
          </p>
          <p className="text-[#A8A9AD]">
            All twelve labelled components are independently sourced, finished and assembled.
          </p>
        </figcaption>
      </figure>

      <p className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD] text-center">
        Reference drawings for the workshop · final tolerances confirmed at CAD review
      </p>
    </section>
  );
}
